import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ChannelUIds, MassNotificationSeverity } from '@app/modules/communications/store/communications';
import { Subject } from 'rxjs/internal/Subject';
import { startWith, takeUntil } from 'rxjs/operators';
import { NotificationPrefsApiService } from '../../store/notification-prefs-api.service';
import { NotificationStatusService } from '@app/modules/communications/services/notification-status-service';
import { RatingArea, RatingScale, RatingScaleLevel } from '@app/modules/management/store/ratings/ratings.model';
import { TranslateService } from '@ngx-translate/core';
import { CultureService } from '@app/modules/core/services/culture.service';
import { ColorUtilsService } from '@app/core/services';

interface MobileDisplayState {
  extreme: boolean;
  high: boolean;
  moderate: boolean;
  low: boolean;
}

@Component({
  selector: 'topo-preference-settings',
  templateUrl: './preference-settings.component.html',
  styleUrls: ['./preference-settings.component.scss'],
  standalone: false
})
export class PreferenceSettingsComponent implements OnInit, OnDestroy {
  @Input() form: FormGroup;
  @Input() set availableChannels(value: string[]) {
    this.hasEmail = value.some(r => r === ChannelUIds.TopoEmailChannelUId || r === ChannelUIds.SendGridEmailChannelUId);
    this.hasText = value.some(r => r === ChannelUIds.TwilioChannelUId || r === ChannelUIds.HorizonSmsChannelUId);
    this.hasPhone = value.some(r => r === ChannelUIds.TwilioTtsChannelUId);
    this.hasSlack = value.some(r => r === ChannelUIds.SlackChannelUId);
    this.hasTeams = value.some(r => r === ChannelUIds.MicrosoftTeamsChannelUId);
    this.hasWhatsApp = value.some(r => r === ChannelUIds.WhatsAppChannelUId);
    this.getRatingScale();
  }
  @Input() isDesktop: boolean;
  destroy$ = new Subject<void>();
  display: MobileDisplayState = {
    extreme: false,
    high: false,
    moderate: false,
    low: false
  };
  hasRequiredFields = {
    work: {
      email: false,
      phone: false
    },
    personal: {
      email: false,
      phone: false
    },
    whatsAppAddress: false
  };

  hasEmail: boolean = true;
  hasText: boolean = true;
  hasPhone: boolean = true;
  hasSlack: boolean = true;
  hasTeams: boolean = true;
  hasWhatsApp: boolean = true;
  ratingScale: RatingScale;
  ratingScalelevels: RatingScaleLevel[];

  constructor(
    private notificationApiService: NotificationPrefsApiService,
    private notificationStatusService: NotificationStatusService,
    private translateService: TranslateService,
    private cultureService: CultureService,
    private colorUtilsService: ColorUtilsService
  ) {}

  ngOnInit(): void {
    this.form.valueChanges
      .pipe(takeUntil(this.destroy$), startWith(this.form.getRawValue()))
      .subscribe(({ work_email, work_phone, personal_email, personal_phone, whatsapp_address }) => {
        this.hasRequiredFields = {
          work: {
            email: Boolean(work_email),
            phone: Boolean(work_phone)
          },
          personal: {
            email: Boolean(personal_email),
            phone: Boolean(personal_phone)
          },
          whatsAppAddress: Boolean(whatsapp_address)
        };
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleSection(severityName: keyof MobileDisplayState) {
    this.display = Object.entries(this.display).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]: key === severityName ? !value : false
      }),
      {}
    ) as MobileDisplayState;
  }

  private getRatingScale(): void {
    this.ratingScale = this.notificationStatusService.ratingScale;
    this.ratingScalelevels = this.ratingScale?.levels.sort((a, b) => b.level - a.level);
  }
}
